import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, AlertTriangle, Repeat } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Empty } from "@/components/app/bits";
import { fmtDate } from "@/components/app/readinessShared";
import AdoptionTab from "@/components/app/AdoptionTab";
import OutcomeDialog from "@/components/app/OutcomeDialog";
import { useAuth } from "@/context/AuthContext";

// Sustain is a lens over existing adoption + metrics, plus the two genuinely-missing things: decision follow-through and the 90-day re-check.
export default function SustainTab({ change, onSaved }) {
  const { can } = useAuth();
  const writer = can("write") && !change.is_example;
  const [data, setData] = useState(null);
  const [showRecheck, setShowRecheck] = useState(false);
  const load = useCallback(() => api.get(`/changes/${change.id}/sustain`).then((r) => setData(r.data)), [change.id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const markMet = async (did, idx) => { try { await api.post(`/decisions/${did}/conditions/${idx}/toggle`); toast.success("Verified as met"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const r = data.recheck || {};
  return (
    <div className="space-y-6" data-testid="sustain-panel">
      {showRecheck && <OutcomeDialog change={change} onDone={() => { setShowRecheck(false); load(); onSaved?.(); }} />}
      <div className="rounded-2xl border border-success-200 bg-success-50 p-4" data-testid="sustain-intro">
        <div className="font-bold text-success-900">Sustain — did it stick?</div>
        <p className="text-sm text-success-900/90 mt-1">This is a lens over your adoption measures and reinforcement, not a new workspace. Two things close the loop: verifying the conditions a decision rode on were actually met, and one honest 90-day answer on whether adoption held.</p>
      </div>

      {(r.due_day90 || r.due_close) && (
        <section className="pp-card p-5 border-ink" data-testid="sustain-recheck">
          <h3 className="font-head text-base font-extrabold flex items-center gap-2"><Repeat size={16} className="text-accent-600" /> {r.due_day90 ? "90-day re-check due" : "Close-out check due"}</h3>
          <p className="text-sm text-subtle mt-1">{r.due_day90 ? "It's about three months since this closed. One honest answer — held, patchy, slid back — is the single most valuable data point the product collects." : "Record how this landed at close."}</p>
          {writer && <button data-testid="sustain-recheck-btn" onClick={() => setShowRecheck(true)} className="pp-btn-dark text-sm mt-3">Answer now</button>}
        </section>
      )}
      {!r.due_day90 && r.day90 && (
        <p className="text-sm text-muted-foreground" data-testid="sustain-recheck-done">90-day re-check recorded{r.day90.outcome ? `: ${r.day90.outcome}` : " (skipped)"}.</p>
      )}

      <section data-testid="sustain-conditions">
        <h3 className="font-head text-lg font-extrabold">Decision follow-through</h3>
        <p className="text-sm text-subtle mt-1">Where a gate decision carried conditions, this is where they're verified as actually met — not merely ticked at the time.</p>
        {data.outstanding_conditions.length === 0 ? <Empty testid="sustain-conditions-empty" text="No outstanding decision conditions. Every condition a decision rode on has been met — or there were none." />
          : <div className="mt-3 space-y-2">{data.outstanding_conditions.map((c) => (
              <div key={`${c.decision_id}-${c.idx}`} className={`pp-card p-3 flex items-start justify-between gap-3 ${c.overdue ? "border-danger-300" : ""}`} data-testid={`sustain-condition-${c.decision_id}-${c.idx}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground">{c.text}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{c.owner ? `${c.owner} · ` : ""}{c.due ? `due ${fmtDate(c.due)}` : "no due date"}{c.overdue && <span className="text-danger-700 font-bold"> · overdue</span>} · from the {c.gate} gate</div>
                </div>
                {writer && <button data-testid={`sustain-markmet-${c.decision_id}-${c.idx}`} onClick={() => markMet(c.decision_id, c.idx)} className="pp-btn-ghost text-sm shrink-0"><Check size={14} /> Verify met</button>}
              </div>))}</div>}
      </section>

      <section data-testid="sustain-adoption">
        <h3 className="font-head text-lg font-extrabold">Adoption & measures</h3>
        <p className="text-sm text-subtle mt-1">Your existing success metrics — the evidence that the new way is holding.</p>
        <div className="mt-3"><AdoptionTab changeId={change.id} /></div>
      </section>
    </div>
  );
}
